/**
 * PropertyResults Component (EN)
 * Visualization and management of cadastral results
 */

export function PropertyResults() {
    return `
        <div id="property-results-container" class="w-full max-w-4xl mx-auto">
            <div id="property-results" class="hidden"></div>
            <div id="no-results" class="hidden">
                <div class="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <p class="text-gray-600">No hay resultados de propiedades disponibles.</p>
                </div>
            </div>
        </div>
    `;
}

export class PropertyResultsManager {
    constructor(containerId = 'property-results-container') {
        this.containerId = containerId;
        this.resultsContainer = null;
        this.noResultsElement = null;
        this.init();
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupElements());
        } else {
            this.setupElements();
        }
    }

    setupElements() {
        const container = document.getElementById(this.containerId);
        if (!container) return;

        this.resultsContainer = container.querySelector('#property-results');
        this.noResultsElement = container.querySelector('#no-results');
    }

    displayResults(result, propertyData, searchType = 'unknown', showCollapsible = true) {
        if (!this.resultsContainer || !this.noResultsElement) return;

        try {
            this.noResultsElement.style.display = 'none';
            this.resultsContainer.style.display = 'block';

            const tableHTML = this.createPropertyTable(result, propertyData, searchType);

            this.resultsContainer.innerHTML = `
<div class="bg-white rounded-lg shadow-lg border border-green-300">
    <div class="p-5 bg-green-100 border-b border-green-300 rounded-t-lg">
        <h3 class="text-xl font-bold text-green-800 mb-2">
            ❌ Property data not found
        </h3>

        <div class="bg-white bg-opacity-70 p-3 rounded-md text-sm space-y-1">
            <p><strong>Search type:</strong> ${searchType}</p>
            <p><strong>Address:</strong>
                ${propertyData.calle || propertyData.street || ''}
                ${propertyData.numero || ''},
                ${propertyData.municipio || propertyData.municipality || ''},
                ${propertyData.provincia || propertyData.province || ''}
            </p>
            <p><strong>Date and time:</strong> ${new Date().toLocaleString('en-GB')}</p>

            <a
                href="https://www1.sedecatastro.gob.es/Cartografia/mapa.aspx?buscar=S"
                target="_blank"
                class="inline-block mt-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded shadow"
            >
                Open official Cadastre map
            </a>
        </div>
    </div>

    <div class="p-5">
        ${tableHTML}

        ${showCollapsible ? `
        <details class="mt-6 bg-white border rounded-lg">
            <summary class="p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 rounded-lg font-medium border-b">
                📋 Full API response (click to expand)
            </summary>
            <div class="p-3 bg-white">
                ${this.generatePropertyStyleTable(result)}
            </div>
        </details>
        ` : ``}
    </div>
</div>
`;

            document.dispatchEvent(new CustomEvent('propertyDataUpdated', {
                detail: { propertyData: { result, propertyData } }
            }));

        } catch (error) {
            this.displayError(error.message, propertyData, searchType);
        }
    }

    displayError(errorMessage, propertyData = {}, searchType = 'unknown') {
        this.resultsContainer.style.display = 'block';
        this.resultsContainer.innerHTML = `
<div class="bg-white rounded-lg shadow-lg border border-red-300">
    <div class="p-5 bg-red-100 border-b border-red-300 rounded-t-lg">
        <h3 class="text-xl font-bold text-red-800 mb-2">
            ❌ Error al obtener los datos
        </h3>
        <div class="p-3 text-sm text-gray-800">
            <p><strong>Tipo de búsqueda:</strong> ${searchType}</p>
            <p><strong>Error:</strong> ${errorMessage}</p>
            <p><strong>Hora:</strong> ${new Date().toLocaleString('es-ES')}</p>
        </div>
    </div>
</div>
        `;
    }

    createPropertyTable(result, propertyData = {}, searchType = 'unknown') {
        const data = result.consulta_dnplocResult || result.consulta_dnprcResult;
        if (!data) return '<p class="text-red-500 p-4">No se encontraron datos en la respuesta de la API.</p>';

        const errors = data.lerr || [];
        const numerero = data.numerero?.nump || [];
        const lrcdnp = data.lrcdnp?.rcdnp || [];

        let html = '<div class="space-y-6">';

        // Advertencias
        if (errors.length > 0) {
            html += `
<div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
    <h4 class="font-semibold text-yellow-800 mb-2">⚠️ Advertencias</h4>
    <ul class="text-sm list-disc list-inside space-y-1">
        ${errors.map(e => `<li><strong>${e.cod}</strong>: ${e.des}</li>`).join('')}
    </ul>
</div>
`;
        }

        // Detección de números duplicados
        let houseNumbers = [];

        numerero.forEach(n => n.num?.pnp && houseNumbers.push(n.num.pnp));
        lrcdnp.forEach(rc => {
            const pnp = rc.dt?.locs?.lous?.lourb?.dir?.pnp;
            if (pnp) houseNumbers.push(pnp);
        });

        const hasDuplicates = new Set(houseNumbers).size < houseNumbers.length;

        const hasErrorCode = errors.some(e => {
            const code = Number(e.cod);
            return code >= 3 && code <= 79;
        });

        if (hasDuplicates || hasErrorCode) {
            this.handleRedirect(result, propertyData, searchType, hasErrorCode ? 'codigo_error' : 'numeros_duplicados');
        }

        html += '</div>';
        return html;
    }

    handleRedirect(result, propertyData, searchType, reason) {
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/cadastro')) return;

        const errorData = {
            result,
            propertyData,
            searchType,
            timestamp: Date.now(),
            redirectReason: reason
        };

        sessionStorage.setItem('cadastroErrorData', JSON.stringify(errorData));

        setTimeout(() => {
            if (window.router?.navigate) {
                window.router.navigate('/cadastro/');
            } else {
                window.location.href = '/cadastro/';
            }
        }, 0);
    }

    generatePropertyStyleTable(data) {
        if (data === null || data === undefined) {
            return `<div class="text-gray-400 italic p-2">null</div>`;
        }

        if (typeof data !== 'object') {
            return `<div class="p-2 text-gray-800">${String(data)}</div>`;
        }

        if (Array.isArray(data)) {
            return `
<table class="min-w-full text-sm">
    <tbody class="divide-y divide-gray-100">
        ${data.map((item, i) => `
        <tr>
            <td class="px-3 py-2 font-medium w-32">[${i}]</td>
            <td class="px-3 py-2">${this.generatePropertyStyleTable(item)}</td>
        </tr>
        `).join('')}
    </tbody>
</table>`;
        }

        return `
<table class="min-w-full text-sm">
    <tbody class="divide-y divide-gray-100">
        ${Object.entries(data).map(([k, v]) => `
        <tr>
            <td class="px-3 py-2 font-medium">${k}</td>
            <td class="px-3 py-2">${this.generatePropertyStyleTable(v)}</td>
        </tr>
        `).join('')}
    </tbody>
</table>`;
    }
}

export default PropertyResultsManager;
